import { supabase } from '../lib/supabase';

export interface RevenueCatProduct {
  id: string;
  title: string;
  description: string;
  price: number;
  priceString: string;
  currency: string;
  subscriptionPeriod: 'monthly' | 'yearly' | 'lifetime';
}

export interface RevenueCatSubscription {
  id: string;
  productId: string;
  status: 'active' | 'cancelled' | 'expired' | 'in_grace_period' | 'on_hold' | 'paused';
  expirationDate: Date | null;
  willRenew: boolean;
  periodType: 'normal' | 'trial' | 'intro';
}

export interface RevenueCatCustomerInfo {
  originalAppUserId: string;
  managementURL: string | null;
  activeSubscriptions: string[];
  allPurchasedProductIdentifiers: string[];
  latestExpirationDate: Date | null;
}

export class RevenueCatService {
  /**
   * Get available products for the current user
   */
  static async getProducts(): Promise<RevenueCatProduct[]> {
    try {
      // In a real implementation, this would call the RevenueCat API
      // For now, we'll return mock data
      return [
        {
          id: 'pro_monthly',
          title: 'Pro Plan',
          description: 'Up to 10 rooms, 50 participants per room, 5 broadcast links',
          price: 4.99,
          priceString: '€4.99',
          currency: 'EUR',
          subscriptionPeriod: 'monthly'
        },
        {
          id: 'pro_yearly',
          title: 'Pro Plan (Yearly)',
          description: 'Save 20% with annual billing',
          price: 47.99,
          priceString: '€47.99',
          currency: 'EUR',
          subscriptionPeriod: 'yearly'
        },
        {
          id: 'enterprise_monthly',
          title: 'Enterprise Plan',
          description: 'Unlimited rooms, 200 participants per room, unlimited broadcast links',
          price: 9.99,
          priceString: '€9.99',
          currency: 'EUR',
          subscriptionPeriod: 'monthly'
        },
        {
          id: 'enterprise_yearly',
          title: 'Enterprise Plan (Yearly)',
          description: 'Save 20% with annual billing',
          price: 95.99,
          priceString: '€95.99',
          currency: 'EUR',
          subscriptionPeriod: 'yearly'
        }
      ];
    } catch (error) {
      console.error('Failed to get products:', error);
      throw error;
    }
  }

  /**
   * Get current user's subscription info
   */
  static async getCurrentSubscription(): Promise<RevenueCatSubscription | null> {
    try {
      // In a real implementation, this would call the RevenueCat API
      // For now, we'll check our database for subscription info
      const { data: subscription, error } = await supabase
        .from('stripe_user_subscriptions')
        .select('*')
        .maybeSingle();

      if (error || !subscription) {
        return null;
      }

      // Map Stripe subscription to RevenueCat format
      return {
        id: subscription.subscription_id || '',
        productId: subscription.price_id || '',
        status: mapStripeStatusToRevenueCat(subscription.subscription_status),
        expirationDate: subscription.current_period_end ? new Date(subscription.current_period_end * 1000) : null,
        willRenew: !subscription.cancel_at_period_end,
        periodType: 'normal'
      };
    } catch (error) {
      console.error('Failed to get current subscription:', error);
      return null;
    }
  }

  /**
   * Get customer info from RevenueCat
   */
  static async getCustomerInfo(): Promise<RevenueCatCustomerInfo | null> {
    try {
      // In a real implementation, this would call the RevenueCat API
      // For now, we'll return mock data based on the current subscription
      const subscription = await this.getCurrentSubscription();
      
      if (!subscription) {
        return {
          originalAppUserId: '',
          managementURL: null,
          activeSubscriptions: [],
          allPurchasedProductIdentifiers: [],
          latestExpirationDate: null
        };
      }

      return {
        originalAppUserId: (await supabase.auth.getUser()).data.user?.id || '',
        managementURL: 'https://app.revenuecat.com/manage',
        activeSubscriptions: subscription.status === 'active' ? [subscription.productId] : [],
        allPurchasedProductIdentifiers: [subscription.productId],
        latestExpirationDate: subscription.expirationDate
      };
    } catch (error) {
      console.error('Failed to get customer info:', error);
      return null;
    }
  }

  /**
   * Purchase a product
   */
  static async purchaseProduct(productId: string): Promise<boolean> {
    try {
      // In a real implementation, this would call the RevenueCat API
      // For now, we'll redirect to our Stripe checkout
      const product = await this.getProductByRevenueCatId(productId);
      if (!product) {
        throw new Error('Product not found');
      }

      // Redirect to Stripe checkout
      window.location.href = `/checkout?product=${product.stripeId}`;
      return true;
    } catch (error) {
      console.error('Failed to purchase product:', error);
      return false;
    }
  }

  /**
   * Restore purchases
   */
  static async restorePurchases(): Promise<boolean> {
    try {
      // In a real implementation, this would call the RevenueCat API
      // For now, we'll just check our database
      const subscription = await this.getCurrentSubscription();
      return !!subscription && subscription.status === 'active';
    } catch (error) {
      console.error('Failed to restore purchases:', error);
      return false;
    }
  }

  /**
   * Check if user has access to a specific entitlement
   */
  static async checkEntitlement(entitlement: string): Promise<boolean> {
    try {
      const subscription = await this.getCurrentSubscription();
      if (!subscription || subscription.status !== 'active') {
        return false;
      }

      // Map product IDs to entitlements
      const entitlementMap: Record<string, string[]> = {
        'pro_monthly': ['pro_features', 'custom_branding', 'analytics'],
        'pro_yearly': ['pro_features', 'custom_branding', 'analytics'],
        'enterprise_monthly': ['pro_features', 'custom_branding', 'analytics', 'sso', 'custom_domain', 'advanced_security'],
        'enterprise_yearly': ['pro_features', 'custom_branding', 'analytics', 'sso', 'custom_domain', 'advanced_security']
      };

      return entitlementMap[subscription.productId]?.includes(entitlement) || false;
    } catch (error) {
      console.error('Failed to check entitlement:', error);
      return false;
    }
  }

  /**
   * Get product by RevenueCat ID
   */
  private static async getProductByRevenueCatId(revenueCatId: string): Promise<{ stripeId: string } | null> {
    // Map RevenueCat product IDs to Stripe product IDs
    const productMap: Record<string, string> = {
      'pro_monthly': 'prod_SSYJTV34liVNzA',
      'pro_yearly': 'prod_SSYJTV34liVNzA',
      'enterprise_monthly': 'prod_SSYY2jEPyVsQhB',
      'enterprise_yearly': 'prod_SSYY2jEPyVsQhB'
    };

    return productMap[revenueCatId] ? { stripeId: productMap[revenueCatId] } : null;
  }
}

/**
 * Map Stripe subscription status to RevenueCat status
 */
function mapStripeStatusToRevenueCat(stripeStatus: string): RevenueCatSubscription['status'] {
  switch (stripeStatus) {
    case 'active':
      return 'active';
    case 'canceled':
      return 'cancelled';
    case 'past_due':
      return 'in_grace_period';
    case 'unpaid':
      return 'on_hold';
    case 'paused':
      return 'paused';
    default:
      return 'expired';
  }
}